"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { OddsDisplay } from "@/lib/odds-screen/live/types";
import { ODDS_DISPLAY_LABELS } from "./view-model";

const OPTIONS: readonly OddsDisplay[] = ["american", "decimal", "percent"];

export function OddsFormatMenu({ format, onFormat }: { format: OddsDisplay; onFormat: (value: OddsDisplay) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) close(); };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, close]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Odds format, ${ODDS_DISPLAY_LABELS[format]}`}
        onClick={() => setOpen((value) => !value)}
        className={cn("tap flex h-8 items-center gap-1 rounded-md px-2 text-[13px] font-medium text-zinc-300 hover:bg-white/[0.04] hover:text-foreground", open && "bg-white/[0.06] text-foreground")}
      >
        <span>{ODDS_DISPLAY_LABELS[format]}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-zinc-500"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div role="menu" aria-label="Odds format" className="absolute right-0 top-[calc(100%+6px)] z-30 w-44 origin-top-right rounded-lg border border-surface-line bg-surface-overlay p-1 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none">
          {OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={format === option}
              onClick={() => { onFormat(option); close(); }}
              className={cn(
                "flex h-8 w-full items-center justify-between gap-3 rounded-md px-2.5 text-[13px] font-medium",
                format === option ? "bg-primary/10 text-primary" : "text-zinc-300 hover:bg-white/[0.06] hover:text-foreground",
              )}
            >
              <span>{ODDS_DISPLAY_LABELS[option]}</span>
              {format === option && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
